import { usePortfolioData } from '@/hooks/use-portfolio-data';
import { Code, Mail, ChevronDown } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import * as Icons from 'lucide-react';
import AnimatedTitle from '@/components/AnimatedTitle';

export function Hero() {
  const { data: portfolioData, isLoading } = usePortfolioData();

  const DynamicIcon = ({ name }: { name: string }) => {
    const LucideIcon = (Icons as any)[name] || Icons.Circle;
    return <LucideIcon className="h-5 w-5" />;
  };

  if (isLoading) {
    return (
      <section id="hero" className="pt-24 lg:pt-0 lg:min-h-screen flex items-center relative overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <Skeleton className="h-6 w-40 mb-2" />
              <Skeleton className="h-12 w-64 mb-4" />
              <Skeleton className="h-8 w-80 mb-6" />
              <Skeleton className="h-20 w-full max-w-lg mb-8" />
              <div className="flex flex-wrap gap-4">
                <Skeleton className="h-12 w-40" />
                <Skeleton className="h-12 w-40" />
              </div>
              <div className="mt-8 flex space-x-4">
                {[1, 2, 3, 4].map(i => (
                  <Skeleton key={i} className="h-10 w-10 rounded-full" />
                ))}
              </div>
            </div>
            <div className="order-1 lg:order-2 flex justify-center">
              <Skeleton className="w-64 h-64 md:w-80 md:h-80 rounded-full" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  console.log('Portfolio Data:', portfolioData);

  if (!portfolioData) {
    console.error('No portfolio data available');
    return null;
  }

  return (
    <section id="hero" className="pt-24 lg:pt-0 lg:min-h-screen flex items-center relative overflow-hidden">
      {/* Enhanced Animated Background with Layered Effects */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {/* Base gradient background */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-secondary/5"></div>

        {/* Large animated color orbs - Adjusted positions to match profile image */}
        <div className="absolute top-0 left-1/4 w-[40rem] h-[40rem] bg-primary/5 rounded-full filter blur-[100px] animate-float" style={{ animationDuration: '30s' }}></div>
        <div className="absolute bottom-0 right-1/4 w-[40rem] h-[40rem] bg-secondary/5 rounded-full filter blur-[100px] animate-float animation-delay-700" style={{ animationDuration: '25s' }}></div>
        <div className="absolute top-1/3 right-0 w-[20rem] h-[20rem] bg-accent/5 rounded-full filter blur-[80px] animate-float animation-delay-300" style={{ animationDuration: '20s' }}></div>

        {/* Medium sized floating elements - Adjusted positions */}
        <div className="absolute top-1/3 left-1/3 w-96 h-96 bg-primary/8 rounded-full filter blur-3xl animate-float animation-delay-500" style={{ animationDuration: '15s' }}></div>
        <div className="absolute bottom-1/3 right-1/3 w-96 h-96 bg-secondary/8 rounded-full filter blur-3xl animate-float" style={{ animationDuration: '18s' }}></div>

        {/* Subtle pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.02] dark:opacity-[0.04]"
          style={{
            backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath opacity='.5' d='M96 95h4v1h-4v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9zm-1 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"
          }}
        ></div>

        {/* Faint code pattern background */}
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1550439062-609e1531270e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&h=1080')] bg-no-repeat bg-center bg-cover opacity-[0.02] dark:opacity-[0.04] mix-blend-overlay"></div>

        {/* Animated particle effect for depth */}
        <div className="absolute inset-0">
          {/* Particle dots - simulated with fixed positioned elements */}
          <div className="absolute h-2 w-2 rounded-full bg-primary/20 animate-float" style={{ top: '15%', left: '10%', animationDuration: '10s' }}></div>
          <div className="absolute h-1 w-1 rounded-full bg-secondary/30 animate-float" style={{ top: '25%', left: '20%', animationDuration: '15s', animationDelay: '1s' }}></div>
          <div className="absolute h-1.5 w-1.5 rounded-full bg-accent/20 animate-float" style={{ top: '65%', left: '15%', animationDuration: '12s', animationDelay: '2s' }}></div>
          <div className="absolute h-2 w-2 rounded-full bg-primary/20 animate-float" style={{ top: '45%', left: '85%', animationDuration: '20s', animationDelay: '0.5s' }}></div>
          <div className="absolute h-1 w-1 rounded-full bg-secondary/30 animate-float" style={{ top: '35%', left: '80%', animationDuration: '18s', animationDelay: '1.5s' }}></div>
          <div className="absolute h-1.5 w-1.5 rounded-full bg-accent/20 animate-float" style={{ top: '70%', left: '75%', animationDuration: '14s', animationDelay: '2.5s' }}></div>
        </div>

        {/* Subtle light rays from corners */}
        <div className="absolute top-0 left-0 w-1/3 h-1/3 bg-gradient-to-br from-primary/5 to-transparent opacity-40"></div>
        <div className="absolute bottom-0 right-0 w-1/3 h-1/3 bg-gradient-to-tl from-secondary/5 to-transparent opacity-40"></div>
      </div>

      {/* Advanced grid pattern with parallax effect */}
      <div
        className="absolute inset-0 z-0 opacity-[0.02] dark:opacity-[0.04] transform scale-150"
        style={{
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.15'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
          animation: 'float 60s infinite linear'
        }}
      ></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pt-16 pb-12 lg:pt-24 lg:pb-0">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <div className="space-y-6">
              <div className="flex items-center space-x-3 mb-6 animate-fade-in">
                <div className="flex items-center space-x-2 px-4 py-2 rounded-full bg-primary/10 dark:bg-primary/20 font-medium text-sm backdrop-blur-sm border border-primary/5 shadow-sm">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-75 animate-ping"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                  </span>
                  <span className="animate-shimmer text-foreground dark:text-foreground/90">Hello, I'm</span>
                </div>

                {/* Decorative line */}
                <div className="h-px w-16 bg-gradient-to-r from-primary/50 to-transparent animate-slide-in-right animate-delay-200"></div>
              </div>

              {/* Main name with enhanced glassmorphism effect */}
              <div className="relative mb-2 group">
                {/* Glassmorphism background for name */}
                <div className="absolute -inset-4 -z-10 rounded-2xl bg-gradient-to-br from-primary/5 to-secondary/5 backdrop-blur-xl border border-white/10 shadow-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                <div className="relative perspective">
                  <h1 className="text-5xl sm:text-6xl lg:text-8xl font-extrabold tracking-tighter animate-scale-up"
                    style={{
                      textShadow: "0 2px 4px rgba(0,0,0,0.1), 0 4px 20px rgba(var(--primary-rgb), 0.2)",
                      WebkitTextStroke: "1px rgba(255,255,255,0.1)",
                      transform: "translateZ(0)"
                    }}>
                    <span className="relative z-10 text-foreground drop-shadow-lg inline-block">
                      {portfolioData.name.split(' ')[0]}
                      <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-accent rounded-full transition-all duration-700 group-hover:w-full"></span>
                    </span>
                    <span className="relative z-10 ml-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent drop-shadow-lg inline-block">
                      {portfolioData.name.split(' ').slice(1).join(' ')}
                      <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-accent to-secondary rounded-full transition-all duration-700 group-hover:w-full"></span>
                    </span>
                  </h1>
                </div>

                {/* Floating particles effect */}
                <div className="absolute -z-20 inset-0 overflow-hidden rounded-2xl">
                  {[...Array(8)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute rounded-full bg-primary/10"
                      style={{
                        width: `${Math.random() * 4 + 2}px`,
                        height: `${Math.random() * 4 + 2}px`,
                        top: `${Math.random() * 100}%`,
                        left: `${Math.random() * 100}%`,
                        animation: `float ${Math.random() * 5 + 5}s ease-in-out infinite`,
                        animationDelay: `${Math.random() * 5}s`,
                        opacity: Math.random() * 0.5 + 0.1
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Job title with animated text */}
              <div className="mb-5 animate-slide-up animate-delay-100 relative">
                <AnimatedTitle />

                {/* Subtle badge */}
                <div className="absolute top-0 -right-12 bg-accent/10 rounded-full px-3 py-0.5 text-xs font-medium text-accent shadow-sm border border-accent/10 animate-fade-in animate-delay-500">
                  Expert
                </div>
              </div>

              {/* Tagline with subtle animation */}
              <div className="relative mb-8 animate-slide-up animate-delay-200 max-w-lg">
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {portfolioData.tagline}
                </p>

                {/* Subtle decoration */}
                <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-primary/20 via-secondary/20 to-transparent rounded-full"></div>
              </div>

              <div className="flex flex-wrap gap-5 animate-slide-up animate-delay-300">
                <a href="#projects" className="inline-block group perspective relative">
                  <div className="absolute -inset-1 bg-gradient-to-r from-primary to-secondary blur-md opacity-70 rounded-xl group-hover:opacity-100 transition-opacity duration-300"></div>
                  <Button
                    className="gap-3 rounded-xl px-6 py-3.5 h-auto text-base font-semibold shadow-lg relative transform transition-all duration-500 animate-pulse-subtle bg-gradient-to-r from-primary to-secondary group-hover:shadow-xl will-change-transform"
                  >
                    <div className="flex items-center justify-center">
                      <Code className="w-6 h-6 mr-2 animate-float animate-duration-2000" />
                      <span>View Projects</span>
                    </div>
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-secondary/10 to-primary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  </Button>
                </a>

                <a href="#contact" className="inline-block group perspective relative">
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-primary/50 to-secondary/50 blur-sm opacity-30 rounded-xl group-hover:opacity-70 transition-all duration-300"></div>
                  <Button
                    variant="outline"
                    className="gap-3 rounded-xl px-6 py-3.5 h-auto text-base font-semibold border-2 shadow-lg relative bg-background group-hover:border-primary/50 group-hover:shadow-xl transition-all duration-300"
                  >
                    <div className="flex items-center justify-center">
                      <div className="relative">
                        <Mail className="w-6 h-6 mr-2 group-hover:animate-wiggle" />
                        <div className="absolute top-0 right-0 w-2 h-2 bg-accent rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
                      </div>
                      <span>Contact Me</span>
                    </div>
                  </Button>
                </a>
              </div>

              <div className="mt-10 animate-slide-up animate-delay-400">
                <p className="text-sm font-medium text-muted-foreground mb-4 flex items-center">
                  <span className="animate-pulse mr-2">•</span> Featured Tech Stack
                </p>
                <div className="flex flex-wrap gap-3">
                  {portfolioData.featuredTech.map((tech, index) => (
                    <div
                      key={tech.name}
                      className="group relative flex items-center gap-2 px-4 py-2 rounded-xl bg-card/80 dark:bg-background/50 backdrop-blur-sm border border-border/50 hover:border-primary/50 transition-all duration-300 hover:shadow-lg"
                      style={{ animationDelay: `${index * 100 + 500}ms` }}
                    >
                      <div className="text-primary group-hover:scale-110 transition-transform duration-300">
                        <DynamicIcon name={tech.icon} />
                      </div>
                      <span className="text-sm font-semibold">{tech.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-10 animate-slide-up animate-delay-500">
                <p className="text-sm font-medium text-muted-foreground mb-3 flex items-center">
                  <span className="animate-pulse mr-2">•</span> Connect with me
                </p>
                <div className="flex space-x-5">
                  {portfolioData.socialLinks.map((link, index) => (
                    <a
                      key={link.url}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={link.name}
                      className="group relative"
                      style={{
                        animationDelay: `${index * 150}ms`
                      }}
                    >
                      {/* Outer glow + animated ring */}
                      <div className="absolute -inset-1.5 bg-gradient-to-r from-primary/50 to-secondary/50 rounded-xl blur-sm opacity-0 group-hover:opacity-90 transition-all duration-300 animate-pulse-subtle animate-delay-300"></div>

                      {/* Button background with gradient border */}
                      <div className="relative p-[1px] rounded-xl bg-gradient-to-r from-primary/50 via-secondary/50 to-accent/50 animate-rotate-in" style={{ animationDelay: `${500 + index * 100}ms` }}>
                        {/* Main button surface */}
                        <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-background backdrop-blur-sm shadow-lg group-hover:shadow-xl transition-all duration-300 overflow-hidden">
                          {/* Icon */}
                          <div className="relative z-10 text-primary group-hover:text-white transition-colors duration-300">
                            <DynamicIcon name={link.icon} />
                          </div>

                          {/* Expanding background fill on hover */}
                          <div className="absolute inset-0 bg-gradient-to-br from-primary to-secondary opacity-0 group-hover:opacity-100 transform scale-0 group-hover:scale-100 transition-all duration-500 rounded-xl"></div>
                        </div>
                      </div>

                      {/* Hidden tooltip */}
                      <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap bg-background border border-border px-2 py-1 rounded text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none shadow-lg">
                        {link.name}
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2 flex justify-center">
            <div className="relative">
              {/* Advanced 3D Decorative Elements */}
              <div className="absolute -inset-1 bg-gradient-to-r from-primary via-secondary to-accent rounded-full blur-xl opacity-70 animate-pulse-subtle"></div>

              {/* Animated floating orbs with varied animations */}
              <div className="absolute -top-8 -right-8 w-28 h-28 bg-accent/30 rounded-full blur-xl animate-float" style={{ animationDuration: '7s' }}></div>
              <div className="absolute -bottom-12 -left-12 w-40 h-40 bg-primary/30 rounded-full blur-xl animate-float animate-delay-300" style={{ animationDuration: '8s' }}></div>
              <div className="absolute top-1/4 -right-4 w-16 h-16 bg-secondary/20 rounded-full blur-lg animate-float animate-delay-700" style={{ animationDuration: '5s' }}></div>

              {/* 3D-like rotating elements */}
              <div className="absolute -z-10 top-1/2 left-0 transform -translate-y-1/2 w-full h-full">
                <div className="absolute top-0 left-1/4 w-8 h-8 border-2 border-primary/30 rounded-md animate-spin-3d" style={{ animationDuration: '15s' }}></div>
                <div className="absolute bottom-1/4 right-1/4 w-10 h-10 border-2 border-secondary/30 rounded-md animate-spin-3d animate-delay-500" style={{ animationDuration: '12s', transformOrigin: 'center' }}></div>
              </div>

              {/* Code bracket decoration with enhanced animation */}
              <div className="absolute -left-10 top-1/2 transform -translate-y-1/2 text-7xl text-primary/20 font-mono font-bold animate-slide-in-left">{`{`}</div>
              <div className="absolute -right-10 top-1/2 transform -translate-y-1/2 text-7xl text-primary/20 font-mono font-bold animate-slide-in-right">{`}`}</div>

              {/* Interactive floating tech icons */}
              <div className="absolute -z-10 inset-0">
                <div className="absolute top-1/4 left-1/4 text-xl text-primary/20 animate-float" style={{ animationDuration: '9s' }}>&#60;/&#62;</div>
                <div className="absolute bottom-1/3 right-1/3 text-xl text-secondary/20 animate-float animate-delay-500" style={{ animationDuration: '7s' }}>#</div>
                <div className="absolute top-2/3 left-1/3 text-xl text-accent/20 animate-float animate-delay-700" style={{ animationDuration: '8s' }}>{ }</div>
              </div>

              {/* Enhanced Image Container with Glassmorphism and 3D effects */}
              <div className="relative z-10 rounded-2xl p-1 bg-gradient-to-br from-primary/30 to-accent/30 shadow-2xl animate-scale-up transform transition-all duration-700 hover:scale-105 hover:rotate-1 group backdrop-blur-sm">
                {/* Multi-layered glow effect with glassmorphism */}
                <div className="absolute -inset-4 bg-gradient-to-br from-primary/10 via-secondary/5 to-accent/10 backdrop-blur-xl rounded-2xl animate-pulse-subtle opacity-70"></div>
                <div className="absolute -inset-1 bg-gradient-to-tr from-primary/20 to-accent/20 backdrop-blur-md rounded-2xl group-hover:backdrop-blur-lg transition-all duration-500"></div>

                {/* Dynamic shimmer overlay with glass effect */}
                <div className="absolute inset-0 rounded-2xl overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-transparent opacity-30 animate-shimmer"></div>
                </div>

                {/* Glass border effect */}
                <div className="absolute inset-0 rounded-2xl border-2 border-white/10 mix-blend-overlay"></div>

                {/* Image with enhanced glass effect */}
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/5 to-accent/5 backdrop-blur-sm">
                  <div className="relative overflow-hidden rounded-2xl">
                    <img
                      src={portfolioData.profileImage}
                      alt={`${portfolioData.name} - Software Engineer`}
                      className="rounded-2xl w-64 h-64 md:w-96 md:h-96 object-cover border-2 border-white/10 relative z-10 transform transition-all duration-700 group-hover:scale-105"
                      style={{
                        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
                        backdropFilter: "blur(10px)"
                      }}
                    />

                    {/* Interactive overlay with glass effect */}
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/50 via-transparent to-transparent opacity-0 group-hover:opacity-70 transition-opacity duration-500 mix-blend-overlay"></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent opacity-0 group-hover:opacity-30 transition-opacity duration-500"></div>
                  </div>

                  {/* Floating elements around the image */}
                  <div className="absolute -z-10 inset-0 overflow-hidden rounded-2xl">
                    {[...Array(12)].map((_, i) => (
                      <div
                        key={i}
                        className="absolute rounded-full bg-white/10 backdrop-blur-sm"
                        style={{
                          width: `${Math.random() * 10 + 5}px`,
                          height: `${Math.random() * 10 + 5}px`,
                          top: `${Math.random() * 100}%`,
                          left: `${Math.random() * 100}%`,
                          animation: `float ${Math.random() * 8 + 4}s ease-in-out infinite`,
                          animationDelay: `${Math.random() * 5}s`,
                          opacity: Math.random() * 0.3 + 0.1
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 hidden lg:block">
        <a href="#about"
          className="w-12 h-12 rounded-full flex items-center justify-center bg-background/80 backdrop-blur-sm border border-border shadow-lg animate-bounce hover:bg-primary/10 transition-colors">
          <ChevronDown className="h-6 w-6 text-primary" />
        </a>
      </div>
    </section>
  );
}

export default Hero;
